import { useState, type FC } from "react";
import styles from "../../styles/ListHeader.module.css";
import { useSelector } from "react-redux";
import { loginSliceValue } from "../../redux/reducers/loginSlice";

interface FilterOption {
  id: string;
  disable?: boolean;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  loading?: boolean;
}

interface ListHeaderProps {
  title: string;
  searchPlaceholder?: string;
  filterOptions?: FilterOption[];
  style?: React.CSSProperties;
  actionButtonText?: string;
  actionButtonIcon?: string;
  onActionButtonClick?: () => void;
  onSearchChange?: (value: string) => void;
  filters?: Record<string, string>;
  setFilters?: (newFilters: Record<string, string>) => void;
  showFilter?: boolean;
  onFilterChange?: (filterId: string, value: string) => void;
  actionType?: string;
  filterSearchClass?: string;
  filterSelectBoxClass?: string;
  onBillingSearchChange?: (value: string) => void;
  onFilterButtonClick?: () => void;
  showBillingCycle?: boolean;
}

const ListHeader: FC<ListHeaderProps> = ({
  title,
  searchPlaceholder,
  filterOptions,
  actionButtonText,
  actionButtonIcon,
  onActionButtonClick,
  onSearchChange,
  style,
  showFilter,
  setFilters,
  actionType,
  filterSearchClass,
  filterSelectBoxClass,
  onBillingSearchChange,
  onFilterButtonClick,
  showBillingCycle,
}) => {
  const { userDetails } = useSelector(loginSliceValue);
  const userRole = userDetails?.data?.user?.role;
  const isClinician = userRole === "clinician";
  const isRestrictedRole = userRole === "clinician" || userRole === "rtmManager" || userRole === "rtmAdmin";

  const [showFilters, setShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [billingSearchValue, setBillingSearchValue] = useState("");
  
  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    () => {
      const initial: Record<string, string> = {};

      filterOptions?.forEach((filter) => {
        const apiKey = filter.value;
        initial[apiKey] =
          filter.label === "Account Owner" && isRestrictedRole
            ? userDetails?.data?.user?._id || ""
            : filter.options[0]?.value || "";
      });
      return initial;
    }
  );

  // useEffect(() => {
  //   const initialFilters: Record<string, string> = {};
  //   filterOptions?.forEach(filter => {
  //     initialFilters[filter.id] = filter.options[0].value;
  //   });
  //   setFilterValues(initialFilters);
  //   setFilters?.(initialFilters); // initialize parent filters on mount
  // }, [filterOptions]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    // onSearchChange?.(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSearchChange?.(searchValue);
      setFilters?.(filterValues);
    }
  };

  const handleBillingSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setBillingSearchValue(value);
    // onBillingSearchChange?.(value);
  };

  const handleFilterChange = (
    filterId: string,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;

    const newFilterValues = {
      ...filterValues,
      [filterId]: value,
    };
    setFilterValues(newFilterValues);
    // setFilters?.(newFilterValues);
  };

  // const handleClearFilters = () => {
  //   setSearchValue("");
  //   setBillingSearchValue("");
  //   setFilterValues({});
  //   onBillingSearchChange?.("");
  //   onSearchChange?.("");
  //   setFilters?.({});
  // };

  const handleClearFilters = () => {
    setSearchValue("");
    setBillingSearchValue("");
    const accountOwnerToPreserve = filterValues.accountowner;
    const defaultFilterValues: Record<string, string> = {};
    filterOptions?.forEach((filter) => {
      const apiKey = filter.value;
      if (apiKey === "accountowner" && (isRestrictedRole || accountOwnerToPreserve)) {
        defaultFilterValues[apiKey] =
          accountOwnerToPreserve ||
          userDetails?.data?.user?._id ||
          filter.options[0]?.value ||
          "";
      } else {
        defaultFilterValues[apiKey] = filter.options[0]?.value || "";
      }
    });
    setFilterValues(defaultFilterValues);
    onBillingSearchChange?.("");
    onSearchChange?.("");
    setFilters?.(defaultFilterValues);
  };


  const toggleFilters = () => {
    if (!showFilters && onFilterButtonClick) {
      onFilterButtonClick();
    }
    setShowFilters((prev) => !prev);
  };

  const containerStyle = {
    fontFamily: "Roboto, sans-serif",
    ...(style || {}),
  };

  const BillingCycleInput = (
    <div
      className={styles.list_header_controls}
      style={{
        flex: userRole === "rtmProvider" ? "0 0 18rem" : "0 0 22rem",
        margin: 0,
      }}
    >
      <div
        className={`${styles.header_text_input} ${filterSearchClass}`}
        style={
          userRole === "rtmProvider"
            ? { flex: "0 1 18rem", minWidth: "15rem" }
            : {}
        }
      >
        <svg className={styles.header_text_input_icon}>
          <use xlinkHref={`/assets/sprite.svg#icon-search-new`} />
        </svg>
        <input
          type="text"
          className={styles.header_text_box}
          placeholder="Billing Cycle - 1, 2, 3 etc."
          value={billingSearchValue}
          onChange={handleBillingSearchChange}
        />
      </div>
    </div>
  );
  return (
    <div className={styles.list_header_container} style={containerStyle}>
      <div className={styles.list_header_top}>
        <h2 className={styles.list_header_title}>{title}</h2>
        <div className={styles.list_header_actions}>
          {showFilter && (
            <div className={styles.tooltip_wrapper}>
              <button className={styles.filter_button} onClick={toggleFilters}>
                <svg>
                  <use xlinkHref={`/assets/sprite.svg#icon-filter-icon`} />
                </svg>
                <span>Filters</span>
              </button>
              <div className={styles.tooltip}>
                {showFilters ? "Collapse Filters" : "Expand Filters"}
              </div>
            </div>
          )}

          {actionButtonText && (
            <button
              className={styles.action_button}
              onClick={onActionButtonClick}
            >
              {actionType === "add" ? (
                <svg
                  className={styles.add_icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              ) : actionType === "back" ? (
                <svg id="icon-back" viewBox="0 0 24 24">
                  <path d="M9 18.984l1.406-1.406-4.594-4.594h16.172v-1.969h-16.172l4.594-4.594-1.406-1.406-6.984 6.984z"></path>
                </svg>
              ) : null}
              <span>{actionButtonText}</span>
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className={styles.list_header_controls}>
          <div
            className={`${styles.header_text_input} ${filterSearchClass}`}
            style={{ flex: "0 0 30rem" }}
          >
            <svg className={styles.header_text_input_icon}>
              <use xlinkHref={`/assets/sprite.svg#icon-search-new`} />
            </svg>
            <input
              type="text"
              className={`${styles.header_text_box} `}
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <div className={`${styles.list_select_box} ${filterSelectBoxClass}`}>
            {filterOptions
              ?.filter((filter) => {
                if (userRole !== "rtmProvider") {
                  return !["Time Spent", "Call Scheduled", "Alerts"].includes(
                    filter.label
                  );
                }
                return true;
              })
              .map((filter) => (
                <div
                  key={filter.id}
                  className={styles.filter_dropdown}
                  style={{ flex: "0 0 22rem" }}
                >
                  <div className={styles.custom_select_container}>
                    <select
                      className={styles.custom_select}
                      value={
                        filterValues[filter.id] !== undefined
                          ? filterValues[filter.id]
                          : filterValues[filter.value] || ""
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          filter.id in filterValues ? filter.id : filter.value,
                          e
                        )
                      }
                      disabled={
                        filter.disable ||
                        (isClinician && filter.label === "Account Owner")
                      }
                    >
                      {filter.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className={styles.select_arrow}>
                      <svg style={{ width: "20px", height: "20px" }}>
                        <use
                          xlinkHref={`/assets/sprite.svg#icon-carte-arrow`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            {userRole === "rtmProvider" && BillingCycleInput}
          </div>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: "1.5rem",
              alignItems: "center",
            }}
          >
            <button
              onClick={handleClearFilters}
              className={`${styles.action_button} ${styles.clear_button}`}
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {showFilters && userRole === "rtmProvider" && (
        <div
          style={{
            marginTop: "1.5rem",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            className={`${styles.action_button} ${styles.search_button}`}
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => {
              onSearchChange?.(searchValue);
              onBillingSearchChange?.(billingSearchValue);
              setFilters?.(filterValues);
            }}
          >
            Search
          </button>
        </div>
      )}

      {showFilters && userRole !== "rtmProvider" && (
        <div
          style={{
            marginTop: "1.5rem",
            display: "flex",
            gap: "1.5rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {filterOptions
            ?.filter(
              (filter) =>
                filter.label === "Time Spent" ||
                (userRole !== "rtmProvider" &&
                  filter.label === "Call Scheduled")
            )
            .map((filter) => {
              const width =
                filter.label === "Call Scheduled" ? "30rem" : "22rem";
              return (
                <div
                  key={filter.id}
                  style={{
                    flex: `0 0 ${width}`,
                    minWidth: width,
                    maxWidth: width,
                  }}
                >
                  <div className={styles.custom_select_container}>
                    <select
                      className={styles.custom_select}
                      value={
                        filterValues[filter.id] !== undefined
                          ? filterValues[filter.id]
                          : filterValues[filter.value] || ""
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          filter.id in filterValues ? filter.id : filter.value,
                          e
                        )
                      }
                      disabled={
                        filter.disable ||
                        (isClinician && filter.label === "Account Owner")
                      }
                    >
                      {filter.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className={styles.select_arrow}>
                      <svg style={{ width: "20px", height: "20px" }}>
                        <use
                          xlinkHref={`/assets/sprite.svg#icon-carte-arrow`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}

          {showBillingCycle && userRole !== "rtmProvider" && BillingCycleInput}

          {filterOptions &&
            userRole !== "rtmProvider" &&
            filterOptions
              ?.filter((filter) => filter.label === "Alerts")
              .map((filter) => (
                <div
                  key={filter.id}
                  style={{ flex: "0 0 22rem", minWidth: "22rem" }}
                >
                  <div className={styles.custom_select_container}>
                    <select
                      className={styles.custom_select}
                      value={filterValues[filter.value] || ""}
                      onChange={(e) => handleFilterChange(filter.value, e)}
                      disabled={
                        filter.disable ||
                        (isRestrictedRole && filter.label === "Account Owner")
                      }
                    >
                      {filter.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className={styles.select_arrow}>
                      <svg style={{ width: "20px", height: "20px" }}>
                        <use
                          xlinkHref={`/assets/sprite.svg#icon-carte-arrow`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
          <button
            type="button"
            className={`${styles.action_button} ${styles.search_button}`}
            style={{
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "auto",
            }}
            onClick={() => {
              onSearchChange?.(searchValue);
              onBillingSearchChange?.(billingSearchValue);
              setFilters?.(filterValues);
            }}
          >
            Search
          </button>
        </div>
      )}
    </div>
  );
};

export default ListHeader;
